import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { QuestionnaireSchedule } from '@stores/QuestionnaireAdminStore';
import {
  useQuestionnaireSchedules,
  useQuestionnaireTimepoints,
} from '@app/view-creator/Admin/QuestionnaireAdminViewCreator';
import { invalidateQuestionnaireScheduleQueries } from '@utils/cacheInvalidation';

/** Runs the schedules page: the schedule list, and which of its dialogs is open. */
export const useQuestionnaireSchedulesController = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const schedulesQuery = useQuestionnaireSchedules();
  const timepointsQuery = useQuestionnaireTimepoints();
  // Undefined when the schedule dialog is closed, null when it's creating a schedule.
  const [editingSchedule, setEditingSchedule] = useState<QuestionnaireSchedule | null | undefined>(undefined);
  const [timepointDialogOpen, setTimepointDialogOpen] = useState(false);

  const handleSaved = () => {
    invalidateQuestionnaireScheduleQueries(queryClient);
    setEditingSchedule(undefined);
    setTimepointDialogOpen(false);
  };

  const schedules = schedulesQuery.data ?? [];
  const timepoints = timepointsQuery.data ?? [];
  // Each timepoint with the names of the schedules it's on, so an unused one is easy to spot.
  const timepointRows = timepoints.map((timepoint) => ({
    ...timepoint,
    scheduleNames: schedules
      .filter((schedule) => schedule.timepoints.some((placement) => placement.timepointId === timepoint.id))
      .map((schedule) => schedule.name),
  }));

  return {
    loading: schedulesQuery.isLoading || timepointsQuery.isLoading,
    loadFailed: schedulesQuery.isError || timepointsQuery.isError || !schedulesQuery.data,
    schedules,
    timepoints,
    timepointRows,
    scheduleDialogOpen: editingSchedule !== undefined,
    editingSchedule: editingSchedule ?? null,
    timepointDialogOpen,
    handleBack: () => navigate('/admin/questionnaires'),
    handleNewSchedule: () => setEditingSchedule(null),
    handleEditSchedule: (schedule: QuestionnaireSchedule) => setEditingSchedule(schedule),
    handleCloseScheduleDialog: () => setEditingSchedule(undefined),
    handleNewTimepoint: () => setTimepointDialogOpen(true),
    handleCloseTimepointDialog: () => setTimepointDialogOpen(false),
    handleSaved,
  };
};
